import { getApartmentEntryPosition } from "./index156.js";
import { getBonesForCoreEntrance } from "./index183.js";
import { GetAllBones, RG_TYPE, getRgObjectsOfType, roundDecimals } from "@project-rouge/rg-core";
import { V3toVector3, applyTransform, getPivot, queryPivot } from "@project-rouge/rg-three";
import { Line3, Vector3 } from "three";
var PARAM_TOL = 1e-5;
var DIST_TOL = .001;
var _projectionPoint = new Vector3();
function validateApartmentEntrancesOnCorridor(building, aggConfig, log) {
	const residentialLevels = building.data.levels.filter((l) => !l.isPodium && !l.isRoof);
	const bones = GetAllBones(building.data.anatomy);
	const checkedRecord = {};
	for (const level of residentialLevels) {
		const levelKey = getLevelKey(building, level.levelIndex);
		if (checkedRecord[levelKey]) continue;
		for (const bone of bones) {
			const boneObjects = getSortedObjectsOnBone(bone, building, level);
			const closingApartments = boneObjects.filter((o) => o.type === RG_TYPE.Apartment && o.data.closeCorridor);
			const closingStarts = closingApartments.filter((o) => o.data.closeDirection[0] < 0);
			const closingEnds = closingApartments.filter((o) => o.data.closeDirection[0] > 0);
			if (bone.muscles.length > 1) {
				const hasStartOnMuscle1 = closingStarts.filter((o) => o.data.muscleID === bone.muscles[0].id).length > 0;
				const hasStartOnMuscle2 = closingStarts.filter((o) => o.data.muscleID === bone.muscles[1].id).length > 0;
				const hasEndOnMuscle1 = closingEnds.filter((o) => o.data.muscleID === bone.muscles[0].id).length > 0;
				const hasEndOnMuscle2 = closingEnds.filter((o) => o.data.muscleID === bone.muscles[1].id).length > 0;
				if (hasStartOnMuscle1 && hasEndOnMuscle2) {
					log && console.log("clashing of closing apartments at start of bone");
					return false;
				}
				if (hasStartOnMuscle2 && hasEndOnMuscle1) {
					log && console.log("clashing of closing apartments at end of bone");
					return false;
				}
			}
			const tendonLine = tendonToLine(bone.tendon);
			extendTendonToIncludeCorner({
				bone,
				tendonLine,
				start: true,
				corridorWidth: aggConfig.corridorWidth
			});
			extendTendonToIncludeCorner({
				bone,
				tendonLine,
				start: false,
				corridorWidth: aggConfig.corridorWidth
			});
			for (const obj of boneObjects) if ([[[
				0,
				0,
				0
			], [
				1,
				0,
				0
			]], [[
				0,
				0,
				1
			], [
				1,
				0,
				1
			]]].map((group) => group.map((pivot) => getEntryPivot({
				obj,
				pivot,
				line: tendonLine
			}))).every((side) => !isOnCorridor(side, tendonLine, aggConfig.corridorWidth))) {
				log && console.log(`entrance not on corridor, object: ${obj.name}`);
				return false;
			}
		}
		checkedRecord[levelKey] = true;
	}
	return true;
}
function getSortedObjectsOnBone(bone, building, level) {
	const boneStart = V3toVector3(bone.joints[0].pos);
	return building.children.filter((c) => {
		if (c.data.level !== level.levelIndex) return false;
		if (c.type === RG_TYPE.Core) {
			const core = c;
			if (core.data.muscleID && bone.muscles.map((m) => m.id).includes(core.data.muscleID)) return true;
			if (core.data.jointID && bone.joints.map((j) => j.id).includes(core.data.jointID)) {
				const joint = bone.joints.find((j) => j.id === c.data.jointID);
				if (!joint) return false;
				return getBonesForCoreEntrance(core, joint).some((b) => b.id === bone.id);
			}
		} else if (c.type === RG_TYPE.Apartment && c.data.muscleID && bone.muscles.map((m) => m.id).includes(c.data.muscleID)) return true;
		return false;
	}).sort((a, b) => {
		const aPos = getEntryPivot({ obj: a });
		const bPos = getEntryPivot({ obj: b });
		return aPos.distanceToSquared(boneStart) - bPos.distanceToSquared(boneStart);
	});
}
function getLevelKey(building, levelId) {
	return building.children.filter((c) => [RG_TYPE.Apartment, RG_TYPE.Core].includes(c.type) && c.data.level === levelId).map((a) => `${a.name},${roundDecimals(a.pos[0])},${roundDecimals(a.pos[2])}`).join(",");
}
function getEntryPivot({ obj, line, pivot = [
	.5,
	0,
	0
] }) {
	if (obj.type === RG_TYPE.Apartment) {
		const posVec$1 = V3toVector3(applyTransform(getApartmentEntryPosition(obj, pivot), obj));
		posVec$1.y = 0;
		return posVec$1;
	}
	if (obj.type === RG_TYPE.Core) {
		const doorsPos = getRgObjectsOfType(obj, RG_TYPE.Door).filter((d) => d.data.isEntrance).map((d) => queryPivot({
			obj,
			pivot,
			type: RG_TYPE.Door,
			query: (c) => c === d
		}));
		doorsPos.forEach((p) => {
			p.y = 0;
		});
		return doorsPos[line ? closestPointToLine(line, doorsPos) : 0];
	}
	const posVec = V3toVector3(getPivot([
		.5,
		0,
		0
	], obj));
	posVec.y = 0;
	return posVec;
}
function isOnCorridor(doorCorners, tendonLine, corridorWidth) {
	const params = doorCorners.map((c) => tendonLine.closestPointToPointParameter(c));
	if (params.some((p) => p < -PARAM_TOL || p > 1 + PARAM_TOL)) return false;
	if (params.map((p, i) => tendonLine.at(p, new Vector3()).distanceTo(doorCorners[i])).some((d) => d > corridorWidth * .5 + DIST_TOL)) return false;
	return true;
}
function closestPointToLine(line, points) {
	if (points.length === 1) return 0;
	return line.closestPointToPoint(points[0], false, _projectionPoint).distanceToSquared(points[0]) < line.closestPointToPoint(points[1], false, _projectionPoint).distanceToSquared(points[1]) ? 0 : 1;
}
function extendTendonToIncludeCorner({ bone, tendonLine, start, corridorWidth }) {
	const i = start ? 0 : 1;
	if (bone.joints[i].bones.length === 1) return;
	const boneB = bone.joints[i].bones.find((b) => b !== bone);
	const tendonB = boneB.tendon;
	const iB = boneB.joints.indexOf(bone.joints[i]);
	const point = new Vector3().fromArray(tendonB.points[iB]);
	const param = tendonLine.closestPointToPointParameter(point);
	if (!(i === 0 && param < 0 || i === 1 && param > 1)) return;
	const halfCorrParam = Math.sign(param) * corridorWidth / tendonLine.distance();
	(!i ? tendonLine.start : tendonLine.end).copy(tendonLine.delta(new Vector3()).multiplyScalar(param + halfCorrParam).add(tendonLine.start));
}
function tendonToLine(tendon) {
	const line = new Line3();
	line.start.fromArray(tendon.points[0]);
	line.end.fromArray(tendon.points[1]);
	return line;
}
export { validateApartmentEntrancesOnCorridor };

//# sourceMappingURL=index184.js.map